import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { continueWithCurrentSession, doLogout, manageSession } from '../actions/session-actions';
import { useNavigateToLoginPrompt } from '../utilities/hooks/history';
import {
  EventAttributes,
  useAnalyticsClient,
  useLinkClickedEvent,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { LoginHintType, useLoginHint } from '../utilities/hooks/login-hint';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import Account from '../components/Account';
import Button from '@atlaskit/button/new';
import sessionsSelector, { Session } from '../selectors/sessions';
import { getApplicationDestination } from '../selectors/application-destination';
import ButtonLink from '../components/ButtonLink';
import UnauthorizedComponent from '../components/UnauthorizedComponent';
import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import { isMobileOidc } from '../utilities/oidc/oidc';
import { RequestAccessLink } from '../components/links';
import SectionMessage from '@atlaskit/section-message';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/fetch-api';
import Heading from '@atlaskit/heading';
import { Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';

const MAXIMUM_ASSOCIATED_SESSIONS = 9;

const styles = cssMap({
  accountsList: {
    marginTop: 0,
    padding: 0,
    listStyleType: 'none',
  },
  accountsListItem: {
    marginTop: 0,
  },
});

const AccountsListWrapper = ({ children }: { children: React.ReactNode }) => (
  <Stack as="ul" xcss={styles.accountsList}>
    {children}
  </Stack>
);

const AccountsListItem = ({ children }: { children: React.ReactNode }) => (
  <li css={styles.accountsListItem}>{children}</li>
);

const pageId = 'selectAccountScreen';

const generateDestUrl = parsedUrl => {
  let destUrl = '';
  if (parsedUrl.pathname) {
    destUrl += parsedUrl.pathname;
  }
  if (parsedUrl.search) {
    destUrl += parsedUrl.search;
  }
  return destUrl;
};

export async function selectAccountLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/login/select-account/state', request, {
    capabilities: ['capability-group--login', 'capability-name--select-account'],
  });
}

/**
 * TODO: Refactor component to separate the UI into a 'dumb' component and then separating the logic, data fetching,
 * bootstrap into a separate 'smart' component.
 * http://go/j/KIRBY-6917
 */
export const SelectAccountPage = () => {
  const navigate = useNavigateSoftOrHard();
  const [searchParams] = useSearchParams();
  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const analyticsClient = useAnalyticsClient();
  const loginHint = useLoginHint();

  const sessions = useAppSelector(state => sessionsSelector(state, search, loginHint));

  const applicationDestination = useMemo(
    () => getApplicationDestination(searchParams),
    [searchParams]
  );

  const hideLogout = useAppSelector(
    state => isMobileOidc(state.microbranding.oidcContext) || state.microbranding.isEmbedded
  );

  const isLoading = useAppSelector(state => state.selectAccount.isLoading);

  const dispatch = useAppDispatch();

  const shouldShowRequestAccessLink = useCallback(() => {
    const continueParam = searchParams.get('continue');
    let parsedUrl = '';
    try {
      parsedUrl = generateDestUrl(new URL(continueParam || ''));
    } catch (error) {
      // Do nothing
    }
    return (
      searchParams.get('showRequestAccessLink') === 'true' &&
      applicationDestination !== null &&
      parsedUrl !== ''
    );
  }, [applicationDestination, searchParams]);

  const eventAttributes = useMemo(
    () => ({
      accountCount: sessions.length,
      loginHintType: loginHint ? loginHint.type : undefined,
      hideLogout: hideLogout,
      requestAccessLinkShown: shouldShowRequestAccessLink(),
      suggestedAccountShown: sessions.some(session => session.suggested),
    }),
    [hideLogout, loginHint, sessions, shouldShowRequestAccessLink]
  );

  usePageViewedEvent(pageId, {
    ...eventAttributes,
    noAccess: !!loginHint && loginHint.type === LoginHintType.NotEmailAddress,
    accounts: sessions.map(session => session.accountId),
  });

  const renderHeader = () => {
    const suggestedAccount = getSuggestedAccount();

    if (sessions.length === 1 && sessions[0].hint === LoginHintType.NotEmailAddress) {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="try.another.account.title"
            defaultMessage="Try a different account"
          />
        </Heading>
      );
    } else if (suggestedAccount) {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage id="select.account.switch.title" defaultMessage="Switch account" />
        </Heading>
      );
    } else {
      return (
        <Heading size="small" as="h1">
          <FormattedMessage
            id="select.account.title"
            defaultMessage="Choose or add another account"
          />
        </Heading>
      );
    }
  };

  const handleClickRequestAccessLink = useLinkClickedEvent(pageId, 'requestAccessLink');

  const renderMainContentFooter = () => {
    const continueParam = searchParams.get('continue');
    let destUrl = '';
    try {
      destUrl = generateDestUrl(new URL(continueParam || ''));
    } catch (error) {
      //
    }
    const requestAccessLink = shouldShowRequestAccessLink() && applicationDestination && (
      <RequestAccessLink
        onClick={handleClickRequestAccessLink}
        destUrl={destUrl}
        applicationDestination={applicationDestination}
      />
    );
    if (hideLogout) {
      return <></>;
    }
    if (requestAccessLink) {
      return requestAccessLink;
    } else {
      if (sessions.length <= 1) {
        return (
          <ButtonLink
            id="redirect-to-logout"
            onClick={async () => {
              await analyticsClient.buttonClickedEvent(
                pageId,
                'forgetAllAccounts',
                eventAttributes
              );
              await dispatch(doLogout());
            }}>
            <FormattedMessage
              id="select.account.footer.redirect.to.logout"
              defaultMessage="Log out"
            />
          </ButtonLink>
        );
      }
    }
  };

  const handleSessionClick = useCallback(
    (
      session: Session,
      eventAttributes: EventAttributes
    ): ((_: React.MouseEvent<HTMLElement>) => void) | null => {
      return () => {
        if (session.current) {
          (async () => {
            await analyticsClient.buttonClickedEvent(
              pageId,
              'continueWithCurrentAccount',
              eventAttributes
            );
            await dispatch(continueWithCurrentSession(navigate));
          })();
        } else {
          analyticsClient.buttonClickedEvent(pageId, 'switchToAccount', eventAttributes);

          const request = { sessionId: session.sessionId, command: 'switch' } as const;
          dispatch(manageSession(request, navigate));
        }
      };
    },
    [analyticsClient, dispatch, navigate]
  );

  const navigateToLogin = useNavigateToLoginPrompt();

  const navigateToLoginPrompt = useCallback((): void => {
    analyticsClient.buttonClickedEvent(pageId, 'addAccount', eventAttributes);
    navigateToLogin();
  }, [analyticsClient, navigateToLogin, eventAttributes]);

  const navigateToRemoveAccountPrompt = useCallback((): void => {
    analyticsClient.buttonClickedEvent(pageId, 'removeAccountFromList', eventAttributes);
    navigate({ pathname: '/login/remove-account', search });
  }, [analyticsClient, eventAttributes, navigate, search]);

  interface LoginHintNotEmailAddressProps {
    session: Session;
    index: number;
    suggestedAccount: Session | undefined;
  }

  const LoginHintNotEmailAddress = ({
    index,
    suggestedAccount,
    session,
  }: LoginHintNotEmailAddressProps) => {
    return suggestedAccount ? (
      <Stack space="space.100">
        <UnauthorizedComponent
          applicationDestination={applicationDestination}
          emailAddress={session.email}
          suggested={true}
        />
        <Account
          id={`account-${suggestedAccount.accountId}`}
          key={`session-${suggestedAccount.sessionId}`}
          primary={true}
          avatarUrl={suggestedAccount.avatarUrl}
          displayName={suggestedAccount.displayName}
          email={suggestedAccount.email}
          isolatedCloudLabel={suggestedAccount.icLabel}
        />
        <TallButton
          appearance="primary"
          onClick={
            handleSessionClick(suggestedAccount, {
              ...eventAttributes,
              accountIndex: index,
            }) || undefined
          }
          isFullWidth
          id="continue">
          <FormattedMessage id="select.account.continue" defaultMessage="Continue" />
        </TallButton>
      </Stack>
    ) : (
      <Stack space="space.100">
        <Account
          id={`account-${session.accountId}`}
          key={`session-${session.sessionId}`}
          primary={true}
          avatarUrl={session.avatarUrl}
          displayName={session.displayName}
          isolatedCloudLabel={session.icLabel}
          email={session.email}
        />
        <UnauthorizedComponent
          applicationDestination={applicationDestination}
          emailAddress={session.email}
          suggested={false}
        />
        {sessions.length === 1 && (
          <TallButton
            id="navigate-to-login-prompt"
            appearance="primary"
            isFullWidth
            onClick={navigateToLoginPrompt}>
            <FormattedMessage id="select.account.add" defaultMessage="Log in to another account" />
          </TallButton>
        )}
      </Stack>
    );
  };

  interface LoginHintProps {
    session: Session;
    index: number;
    suggestedAccount: Session | undefined;
    handleOnClick: ((_: React.MouseEvent<HTMLElement>) => void) | null;
  }

  const LoginHint = ({ session, index, suggestedAccount, handleOnClick }: LoginHintProps) => {
    if (!session.hint) {
      return null;
    }
    switch (session.hint) {
      case LoginHintType.EmailAddress:
        if (handleOnClick) {
          return (
            <Stack space="space.100">
              <Account
                id={`account-${session.accountId}`}
                key={`session-${session.sessionId}`}
                primary={true}
                avatarUrl={session.avatarUrl}
                displayName={session.displayName}
                isolatedCloudLabel={session.icLabel}
                email={session.email}
              />
              <TallButton appearance="primary" onClick={handleOnClick} isFullWidth id="continue">
                <FormattedMessage id="select.account.continue" defaultMessage="Continue" />
              </TallButton>
            </Stack>
          );
        }
        break;
      case LoginHintType.NotEmailAddress:
        return (
          <LoginHintNotEmailAddress
            index={index}
            suggestedAccount={suggestedAccount}
            session={session}
          />
        );
      default:
        return null;
    }
    return null;
  };

  const renderAccounts = () => {
    const suggestedAccount = getSuggestedAccount();

    const accountsList = sessions.map((session, index) => {
      const { accountId, displayName, email, icLabel } = session;

      const handleOnClick = handleSessionClick(session, {
        ...eventAttributes,
        accountIndex: index,
      });

      return session.hint ? (
        <LoginHint
          key="session-hint"
          session={session}
          index={index}
          suggestedAccount={suggestedAccount}
          handleOnClick={handleOnClick}
        />
      ) : (
        !session.suggested && (
          <AccountsListItem key={`session-${session.sessionId}`}>
            <Account
              id={`account-${accountId}`}
              onClick={handleOnClick || undefined}
              avatarUrl={session.avatarUrl}
              displayName={displayName}
              email={email}
              isolatedCloudLabel={icLabel}
            />
          </AccountsListItem>
        )
      );
    });

    const hasMultipleSessions = sessions.length > 1;
    const noLoginHint = sessions.length >= 1 && sessions[0].hint !== LoginHintType.NotEmailAddress;
    const canAddSessions =
      (hasMultipleSessions || noLoginHint) && sessions.length <= MAXIMUM_ASSOCIATED_SESSIONS;

    const loginHintAccountIndex = sessions.findIndex(session => session.hint);
    const loginHintAccount =
      loginHintAccountIndex >= 0 ? accountsList.splice(loginHintAccountIndex, 1) : [];

    return (
      <Stack space="space.100">
        {loginHintAccount}

        {accountsList.length > 0 && <AccountsListWrapper>{accountsList}</AccountsListWrapper>}

        <Stack space="space.050">
          {canAddSessions && (
            <Button
              id="navigate-to-login-prompt"
              testId="add-another-account-button-idf-testid"
              shouldFitContainer
              iconBefore={PersonAvatarIcon}
              onClick={navigateToLoginPrompt}>
              <FormattedMessage id="select.account.another" defaultMessage="Add another account" />
            </Button>
          )}
          {hasMultipleSessions && (
            <Button
              id="navigate-to-remove-account-prompt"
              testId="navigate-to-remove-account-button-idf-testid"
              shouldFitContainer
              iconBefore={CrossCircleIcon}
              onClick={navigateToRemoveAccountPrompt}>
              <FormattedMessage
                id="select.account.footer.navigate.to.remove.account"
                defaultMessage="Remove accounts from this browser"
              />
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const getSuggestedAccount = () => sessions.find(session => session.suggested);

  if (sessions.length > 0) {
    return (
      <WhiteboxPage
        pageId={pageId}
        header={renderHeader()}
        whiteboxFooter={renderMainContentFooter()}
        isLoading={isLoading}>
        <Stack space="space.100">
          {renderAccounts()}

          {sessions.length > MAXIMUM_ASSOCIATED_SESSIONS && (
            <SectionMessage testId="selectaccountmaximumassociatedsessions" appearance="warning">
              <FormattedMessage
                id="select.account.maximum.associated.sessions"
                defaultMessage="You can only add up to 10 accounts. Remove at least one account from this browser to add another."
              />
            </SectionMessage>
          )}
        </Stack>
      </WhiteboxPage>
    );
  }

  navigateToLoginPrompt();
  return null;
};

export default SelectAccountPage;
